import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { eventsApi } from './api';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  localInputToIso,
  rangeBounds,
  withinProjectRange,
} from '@/features/project/planner/dates';
import { Collapsible } from '@/components/ui/Collapsible';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  DURACIONES,
  DURACION_POR_DEFECTO,
  sumarHoras,
  type Duracion,
} from './duracion';
import { toast } from '@/components/ui/Toast';
import { AdvancedScaffold, type ScaffoldPlan } from './AdvancedScaffold';
import { AddressField } from '@/components/forms/AddressField';
import type { Lugar } from '@/lib/maps/googleMaps';
import { useT } from '@/lib/i18n/useT';
import { firstInvalidControl } from '@/lib/i18n/nativeValidation';
import type { XkEvent } from '@/lib/api/types';


/**
 * Right-panel create form for an event. An event always belongs to a project
 * (xk-event-group), so the form leads with a project picker scoped to the
 * brand. Reused by the brand home "Add events" card via FormPanelHost.
 */
export function EventForm({
  brandId,
  projectId,
  initialDate,
  onCreated,
  onCancel,
}: {
  brandId: number;
  /** Optional pre-selected project. */
  projectId?: number;
  /**
   * Fecha de arranque, en el protocolo del DatePicker (`YYYY-MM-DDTHH:mm`).
   *
   * La manda el calendario: quien pulsa el «+» del día 14 ya dijo cuándo, y
   * volver a pedírselo es hacerle repetir lo que acaba de decir.
   */
  initialDate?: string;
  onCreated: (event: XkEvent) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();

  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId),
    queryFn: () => eventsApi.listGroups({ companyId: brandId }),
    enabled: Number.isFinite(brandId) && brandId > 0,
  });
  const projects = groupsQ.data?.items ?? [];

  const [groupId, setGroupId] = useState<number | ''>(projectId ?? '');
  const [name, setName] = useState('');
  /** Dirección **y punto**: lo que devuelve el selector de lugares. */
  const [lugar, setLugar] = useState<Lugar>({
    address: '',
    latitude: null,
    longitude: null,
    placeId: null,
  });
  const [startLocal, setStartLocal] = useState(initialDate ?? '');
  /*
   * Se elige DURACIÓN, no fecha de fin.
   *
   * Nadie monta una activación pensando «termina el jueves a las 21:00»: la
   * piensa como «son cuatro horas». Pedir la fecha de fin obligaba a hacer esa
   * cuenta a mano y a repetirla cada vez que se movía el inicio.
   */
  const [duracion, setDuracion] = useState<Duracion>(DURACION_POR_DEFECTO);
  const endLocal = startLocal ? sumarHoras(startLocal, duracion) : '';
  // El tope del proyecto llega con la lista de proyectos, después del primer
  // render; la validación de rango se hace abajo, contra `bounds`.

  // Default the select to the first project once they load (unless preselected).
  useEffect(() => {
    if (groupId === '' && projects.length > 0) setGroupId(projectId ?? projects[0].id);
  }, [projects, groupId, projectId]);

  // The chosen project's date range clamps the event's start/end. Empty range
  // (project has no dates) means no clamp — the backend stays authoritative.
  const selectedProject = projects.find((p) => p.id === Number(groupId));
  const bounds = rangeBounds(selectedProject?.startDate, selectedProject?.endDate);

  /*
   * El modo avanzado, cerrado por defecto.
   *
   * Crear un evento sigue siendo nombre y fechas. Esto es para quien ya sabe
   * que va a montar tres estaciones con 500 gorras repartidas y prefiere no
   * dar quince pasos después — pero nadie está obligado a abrirlo, y con el
   * desplegable cerrado el formulario es exactamente el de siempre.
   */
  const [avanzado, setAvanzado] = useState(false);
  const [plan, setPlan] = useState<ScaffoldPlan>({
    events: 1,
    stationsPerEvent: 1,
    stock: [],
  });

  const create = useMutation({
    mutationFn: async () => {
      // Con el avanzado abierto lo monta el backend de una vez: valida el
      // cupo del plan y el stock de la marca ANTES de escribir nada, así que
      // lo que no cabe falla sin dejar un evento suelto a medio montar.
      if (avanzado) {
        const r = await eventsApi.scaffold({
          eventGroupId: Number(groupId),
          companyId: brandId,
          event: {
            name: name.trim(),
            address: lugar.address.trim(),
            ...(localInputToIso(startLocal) ? { startDate: localInputToIso(startLocal)! } : {}),
            ...(localInputToIso(endLocal) ? { endDate: localInputToIso(endLocal)! } : {}),
          },
          latitude: lugar.latitude,
          longitude: lugar.longitude,
          placeId: lugar.placeId,
          stationsPerEvent: plan.stationsPerEvent,
          stock: plan.stock.filter((l) => l.quantityPerEvent > 0),
        });
        // Lo que no cupo entero se dice; descartarlo en silencio dejaría al
        // usuario creyendo que el reparto salió como pidió.
        r.notes.forEach((n) => toast({ tone: 'info', title: n }));
        return r.events[0];
      }

      const created = await eventsApi.createEvent({
        name: name.trim(),
        // Send '' (not undefined) so the create succeeds even where the
        // backend `address` column is still NOT NULL without a default.
        address: lugar.address.trim(),
        // El punto sólo si se eligió: media coordenada no es un sitio.
        ...(lugar.latitude != null && lugar.longitude != null
          ? { latitude: lugar.latitude, longitude: lugar.longitude, placeId: lugar.placeId }
          : {}),
        startDate: localInputToIso(startLocal),
        endDate: localInputToIso(endLocal),
        eventGroupId: Number(groupId),
        companyId: brandId,
        sectionCount: 1,
        eventStatus: 'Active',
      });
      // Un evento nuevo ya no arrastra una journey antigua. Lo que ve el
      // invitado sale del despliegue de Xurvey, y crear aquí un `BsEvent`
      // dejaría el evento con un editor que no manda y sin enlace que
      // funcione. La encuesta se elige y se despliega desde la pestaña
      // Encuesta del evento, o de una vez con el quick start.
      return created;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byGroup(Number(groupId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byBrand(brandId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      // El avanzado también crea estaciones y reparte stock: sin invalidar
      // esas dos, el evento aparece recién creado y vacío.
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      // Por prefijo: `stockAllocations` no tiene `all` y las variantes
      // (byBrand/byEvent/byStation) comparten la raíz.
      queryClient.invalidateQueries({ queryKey: ['stock-allocations'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(brandId) });
      toast({ tone: 'success', title: `Event "${created.name}" created` });
      onCreated(created);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: 'Could not create event', description: e.message }),
  });

  const canSubmit =
    name.trim().length >= 2 && groupId !== '' && !create.isPending;

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const invalido = firstInvalidControl(e.currentTarget, t);
    if (invalido) {
      toast({ tone: 'error', title: invalido.message });
      return;
    }
    if (!canSubmit) return;
    const startIso = localInputToIso(startLocal);
    const endIso = localInputToIso(endLocal);
    if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      toast({ tone: 'error', title: t('eventForm.badRange') });
      return;
    }
    if (!withinProjectRange(startIso, endIso, selectedProject?.startDate, selectedProject?.endDate)) {
      toast({ tone: 'error', title: t('eventForm.outOfRange') });
      return;
    }
    create.mutate();
  };

  // No project yet — events need one. Nudge the user to create a project first.
  if (!groupsQ.isLoading && projects.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-[var(--color-xk-text-muted)]">
          {t('eventForm.noProjects')}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          {t('stock.dialog.cancel')}
        </button>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/*
        LO QUE EL EVENTO SE LLEVA DEL PLAN, dicho antes de crearlo.
        Un evento nace con su primera estación y esa estación gasta una del cupo:
        en Free quedan dos, así que el segundo evento deja al cliente sin
        estaciones. Nadie lo decía y se descubría con un 409 (fricción 10).
      */}
      <p className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2 text-[11px] leading-snug text-[var(--color-xk-text-muted)]">
        {t('eventForm.stationNote')}
      </p>

      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t('eventForm.project')} <span className="text-[var(--color-accent-ink)]">*</span>
        </div>
        <select
          value={groupId}
          onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] focus:border-[var(--color-accent-ink)] focus:outline-none"
        >
          {groupsQ.isLoading && <option value="">{t('common.loading')}</option>}
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t('eventForm.name')} <span className="text-[var(--color-accent-ink)]">*</span>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('eventForm.namePlaceholder')}
          autoFocus
          required
          minLength={2}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none"
        />
      </label>

      <AddressField
        label={t('eventForm.address')}
        placeholder={t('eventForm.addressPlaceholder')}
        value={lugar}
        onChange={setLugar}
      />

      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            {t('eventForm.starts')}
          </div>
          <DatePicker
            mode="datetime"
            value={startLocal}
            onChange={setStartLocal}
            min={bounds.min}
            max={endLocal || bounds.max}
          />
        </label>
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            {t('eventForm.duration')}
          </div>
          <select
            value={duracion}
            onChange={(e) => setDuracion(Number(e.target.value) as Duracion)}
            className="h-10 w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-bg-input)] px-3 text-sm text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
          >
            {DURACIONES.map((h) => (
              <option key={h} value={h}>
                {t('eventForm.hours', { n: String(h) })}
              </option>
            ))}
          </select>
          {/* Cuándo termina, dicho en claro. Se quitó el campo, no el dato:
              sin esto habría que hacer la suma de cabeza para saber si el
              evento cruza la medianoche o se sale del proyecto. */}
          {endLocal ? (
            <p className="mt-1 text-[11px] text-[var(--color-fg-muted)]">
              {t('eventForm.endsAt', { fecha: endLocal.replace('T', ' ') })}
            </p>
          ) : null}
        </label>
      </div>

      <Collapsible
        open={avanzado}
        onOpenChange={setAvanzado}
        label={t('scaffold.advanced')}
        className="rounded-lg border border-[var(--color-xk-border)] p-3"
      >
        <AdvancedScaffold brandId={brandId} plan={plan} onPlan={setPlan} />
      </Collapsible>

      <div className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-[var(--color-xk-border)] pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          {t('stock.dialog.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {create.isPending ? t('stock.dialog.saving') : t('eventForm.create')}
        </button>
      </div>
    </form>
  );
}

